import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Card, CardBody, CardFooter, CardHeader, Chip, Divider } from '@heroui/react';
import { useAuth } from '@/lib/auth/hooks/useAuth';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import useAxios from '@/machinery/useAxios';

const TIER_LABELS = {
    BASIC_INDIVIDUAL: 'Basic Caddy - Individual',
    ADVANCED_INDIVIDUAL: 'Advanced Caddy - Individual',
    MASTER_INDIVIDUAL: 'Master Caddy - Individual',
    BASIC_TEAM: 'Basic Caddy - Team',
    ADVANCED_TEAM: 'Advanced Caddy - Team',
    MASTER_TEAM: 'Master Caddy - Team',
};

export const Route = createFileRoute('/account/subscription-plan')({
    component: RouteComponent,
});

function RouteComponent() {
    const { user, refetch } = useAuth();

    const plan = useMemo(() => {
        if (!user) return undefined;
        if (user.subscription_plan) return user.subscription_plan;

        if (user.active_team_plan) return user.active_team_plan;

        return undefined;
    }, [user]);

    const isOwner = useMemo(() => {
        if (!user) return false;
        return user.subscription_plan?.id === plan?.id;
    }, [plan, user]);

    const { ax, loading } = useAxios();

    const cancelSubscription = useCallback(async () => {
        await ax({
            url: `/integrations/stripe-subscription`,
            method: 'delete',
            callback: () => {
                refetch();
            },
        });
    }, [ax, refetch]);
    if (!user) return null;

    return (
        <Card className='max-w-120 mx-auto mt-4'>
            {plan ? (
                <>
                    <CardHeader>
                        <div className='grid w-full items-center justify-items-center'>
                            <div className='text-center text-2xl font-semibold'>Active Subscription</div>
                        </div>
                    </CardHeader>

                    <Divider />
                    <CardBody>
                        <div className='flex flex-row items-center justify-between'>
                            <Chip color='primary' size='lg'>
                                {TIER_LABELS[plan.tier]}
                            </Chip>
                            <div>
                                {plan.in_free_trial_period
                                    ? 'Trial ends'
                                    : plan.cancel_at_period_end
                                    ? 'Ends'
                                    : 'Auto-renews'}{' '}
                                on {dayjs(plan.end_date).format('MMM DD, YYYY')}
                            </div>
                        </div>
                        {isOwner && (
                            <div className='mt-6 w-full'>
                                {!plan.cancel_at_period_end ? (
                                    <Button
                                        color='danger'
                                        className='w-full'
                                        size='sm'
                                        onPress={cancelSubscription}
                                        isLoading={loading}>
                                        Cancel Auto-Renew
                                    </Button>
                                ) : (
                                    <div className='text-center text-sm italic'>
                                        Plan has been cancelled and will end on{' '}
                                        {dayjs(plan.end_date).format('MMM DD, YYYY')}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardBody>
                    <Divider />
                    <CardFooter>
                        <div className='flex w-full flex-col items-center justify-center text-center text-sm'>
                            Having issues or want to upgrade your plan?
                            <div>
                                Please contact{' '}
                                <span className='font-semibold text-green-500'>chandler@caddystats.golf</span>{' '}
                                for support!
                            </div>
                        </div>
                    </CardFooter>
                </>
            ) : (
                <>
                    <CardHeader>
                        <div className='grid w-full items-center justify-items-center'>
                            <div className='text-center text-2xl font-semibold'>No Subscription Found</div>
                            <div className='mb-12 text-center'>
                                It looks like you do not have an active subscription.
                            </div>
                            <Link to='/pricing' className='w-full text-center'>
                                <Button color='primary' className='font-semibold'>
                                    Subscribe Now!
                                </Button>
                            </Link>
                        </div>
                    </CardHeader>
                    <Divider />
                    <CardFooter>
                        <div className='flex w-full flex-col items-center justify-center text-center text-sm'>
                            Already have a subscription?
                            <div>
                                Please contact{' '}
                                <span className='font-semibold text-green-500'>chandler@caddystats.golf</span>{' '}
                                for support!
                            </div>
                        </div>
                    </CardFooter>
                </>
            )}
        </Card>
    );
}
